import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Waves, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useSimplify } from "./simplify-context";
import { CallbackFields } from "./CallbackFields";
import { CALLBACK_COPY, callbackError, callbackSummary, visitorTimezone } from "../../data/callbackCopy";

function trackFunnel(payload) {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/funnel/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch { /* analytics must never break the modal */ }
}

const STRINGS = {
  en: {
    ariaLabel: "Simplification review request",
    title: "Show me where I can simplify",
    intro: (step) =>
      `A free 15-minute business review. Tell us how things run today; we'll show you the simplest thing to fix first. Step ${step} of 2.`,
    namePh: "Your name",
    emailPh: "Work email",
    continue: "Continue",
    privacy: "We'll only use this to reply to you. Nothing else.",
    companyPh: "Company (optional)",
    industries: ["Construction", "Professional services", "Real estate", "Other"],
    drains: [
      "Chasing status updates",
      "Re-entering the same data",
      "Building reports by hand",
      "Not sure — help me find it",
    ],
    submit: "Request my free 15-minute review",
    sending: "Sending…",
    back: "Back",
    topics: ["Not sure — whoever fits", "Zoho CRM expert", "Zoho FSM expert", "Zoho Projects expert", "Zoho Books expert"],
    followupLabel: "How should we follow up?",
    followupEmail: "A written reply within one business day",
    followupPhone: "A voice call from the right expert",
    phonePh: "Best number for the call",
    toastPhone: "Add a phone number so our expert can call you.",
    expectations: [
      "Who it's for: owners and ops leads of 5–50 person service, construction or field businesses.",
      "Bring: one recent job story and a rough guess of the hours it costs you.",
      "You leave with: the simplest thing to fix first — and an honest no if there's no fit.",
      "Nothing continues unless you ask it to.",
    ],
    successTitle: "Consider it on our desk.",
    successBody: "No sequence, no pressure. Just a person who knows where hours hide.",
    successSteps: [
      "Within one business day, Bruno replies personally — a couple of questions about where your team's time goes.",
      "If it sounds like a fit, one short call. If not, you'll hear that honestly.",
      "When you're ready: a free Process Mapping Analysis Audit (up to two meetings), and the first live answer usually inside ten minutes.",
    ],
    toastSuccess: "Request received — we'll be in touch shortly.",
    toastError: "Something didn't go through. Please try again.",
  },
  fr: {
    ariaLabel: "Demande de revue de simplification",
    title: "Montrez-moi où simplifier",
    intro: (step) =>
      `Une revue d'affaires gratuite de 15 minutes. Montrez-nous comment ça roule aujourd'hui; on vous montre la chose la plus simple à régler d'abord. Étape ${step} de 2.`,
    namePh: "Votre nom",
    emailPh: "Courriel professionnel",
    continue: "Continuer",
    privacy: "On s'en sert seulement pour vous répondre. Rien d'autre.",
    companyPh: "Entreprise (facultatif)",
    industries: ["Construction", "Services professionnels", "Immobilier", "Autre"],
    drains: [
      "Courir après les mises à jour",
      "Ressaisir les mêmes données",
      "Monter des rapports à la main",
      "Pas certain — aidez-moi à le trouver",
    ],
    submit: "Demander ma revue gratuite de 15 minutes",
    sending: "Envoi…",
    back: "Retour",
    topics: ["Pas certain — la bonne personne", "Expert Zoho CRM", "Expert Zoho FSM", "Expert Zoho Projects", "Expert Zoho Books"],
    followupLabel: "Comment devrait-on vous répondre ?",
    followupEmail: "Une réponse écrite en un jour ouvrable",
    followupPhone: "Un appel téléphonique de l'expert approprié",
    phonePh: "Le meilleur numéro pour l'appel",
    toastPhone: "Ajoutez un numéro pour qu'on puisse vous appeler.",
    expectations: [
      "Pour qui : dirigeants et responsables des opérations d'entreprises de 5 à 50 personnes — services, construction ou terrain.",
      "Apportez : l'histoire d'un contrat récent et une idée approximative des heures qu'il vous coûte.",
      "Vous repartez avec : la chose la plus simple à régler d'abord — et un non honnête si ce n'est pas un fit.",
      "Rien ne continue sans que vous le demandiez.",
    ],
    successTitle: "C'est sur notre bureau.",
    successBody: "Pas de séquence, pas de pression. Juste une personne qui sait où les heures se cachent.",
    successSteps: [
      "En un jour ouvrable, Bruno vous répond personnellement — deux ou trois questions sur où va le temps de votre équipe.",
      "Si ça semble un bon fit, un court appel. Sinon, on vous le dit honnêtement.",
      "Quand vous êtes prêt : un audit d'analyse de processus gratuit (jusqu'à deux rencontres); la première réponse en direct arrive souvent en moins de dix minutes.",
    ],
    toastSuccess: "Demande reçue — on vous répond très vite.",
    toastError: "Quelque chose n'a pas passé. Réessayez, SVP.",
  },
  es: {
    ariaLabel: "Solicitud de revisión de simplificación",
    title: "Muéstreme dónde simplificar",
    intro: (step) =>
      `Una revisión de negocio gratuita de 15 minutos. Muéstrenos cómo funciona hoy; le mostramos lo más simple de arreglar primero. Paso ${step} de 2.`,
    namePh: "Su nombre",
    emailPh: "Correo de trabajo",
    continue: "Continuar",
    privacy: "Solo lo usamos para responderle. Nada más.",
    companyPh: "Empresa (opcional)",
    industries: ["Construcción", "Servicios profesionales", "Bienes raíces", "Otro"],
    drains: [
      "Perseguir actualizaciones de estado",
      "Reingresar los mismos datos",
      "Armar reportes a mano",
      "No estoy seguro — ayúdenme a encontrarlo",
    ],
    submit: "Solicitar mi revisión gratuita de 15 minutos",
    sending: "Enviando…",
    back: "Atrás",
    topics: ["No estoy seguro — la persona adecuada", "Experto en Zoho CRM", "Experto en Zoho FSM", "Experto en Zoho Projects", "Experto en Zoho Books"],
    followupLabel: "¿Cómo le damos seguimiento?",
    followupEmail: "Una respuesta escrita en un día hábil",
    followupPhone: "Una llamada de voz del experto adecuado",
    phonePh: "El mejor número para la llamada",
    toastPhone: "Agregue un número de teléfono para que el experto pueda llamarle.",
    expectations: [
      "Para quién: dueños y líderes de operaciones de empresas de 5 a 50 personas — servicios, construcción o campo.",
      "Traiga: la historia de un trabajo reciente y una idea aproximada de las horas que le cuesta.",
      "Se lleva: lo más simple de arreglar primero — y un no honesto si no hay encaje.",
      "Nada continúa a menos que usted lo pida.",
    ],
    successTitle: "Ya está sobre nuestro escritorio.",
    successBody: "Sin secuencias, sin presión. Solo una persona que sabe dónde se esconden las horas.",
    successSteps: [
      "En un día hábil, Bruno le responde personalmente — dos o tres preguntas sobre adónde va el tiempo de su equipo.",
      "Si parece un buen encaje, una llamada corta. Si no, se lo diremos con honestidad.",
      "Cuando esté listo: una auditoría gratuita de análisis de procesos (hasta dos reuniones); la primera respuesta en vivo suele llegar en menos de diez minutos.",
    ],
    toastSuccess: "Solicitud recibida — le respondemos muy pronto.",
    toastError: "Algo no salió bien. Inténtelo de nuevo, por favor.",
  },
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-brand-navy placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20";

export default function SimplifyModal() {
  const { open, setOpen } = useSimplify();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";
  const t = STRINGS[locale];
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", company: "", industry: t.industries[0], time_drain: t.drains[0], topic: t.topics[0], followup: "email", phone: "", callback_date: "", callback_window: "", callback_timezone: visitorTimezone() });
  const [submitError, setSubmitError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const close = () => {
    trackFunnel({ event: "modal_closed", locale, source: pathname, step, meta: done ? "success" : "abandoned" });
    setOpen(false);
    setTimeout(() => { setDone(false); setStep(1); }, 300);
  };

  useEffect(() => {
    if (open) trackFunnel({ event: "modal_opened", locale, source: pathname, step: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { setSubmitError(""); }, [form]);

  useEffect(() => {
    setForm((previous) => ({ ...previous, industry: t.industries[0], time_drain: t.drains[0], topic: t.topics[0] }));
  }, [t]);

  const submit = async (e) => {
    e.preventDefault();
    const error = callbackError(form);
    if (error) {
      setSubmitError(CALLBACK_COPY[locale].errors[error]);
      return;
    }
    setSubmitError("");
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        name: form.name,
        email: form.email,
        company: form.company,
        industry: form.industry,
        time_drain: form.time_drain,
        topic: form.topic,
        callback: form.followup === "phone",
        phone: form.phone,
        callback_date: form.followup === "phone" ? form.callback_date : "",
        callback_window: form.followup === "phone" ? form.callback_window : "",
        callback_timezone: form.followup === "phone" ? form.callback_timezone : "",
        locale,
        source: pathname,
      });
      trackFunnel({ event: "modal_submitted", locale, source: pathname, step: 2, meta: form.followup === "phone" ? "voice" : "email" });
      setDone(true);
      toast.success(t.toastSuccess);
    } catch (err) {
      const message = CALLBACK_COPY[locale].errors[err.response?.data?.detail] || t.toastError;
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const canContinue = form.name.trim() && /\S+@\S+\.\S+/.test(form.email);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="simplify-modal"
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div data-testid="simplify-backdrop" className="absolute inset-0 bg-brand-navy/50 backdrop-blur-sm" onClick={close} />

          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            data-testid="simplify-dialog"
            className="relative w-full max-w-lg min-w-0 bg-white rounded-t-3xl sm:rounded-3xl p-7 sm:p-10 shadow-2xl max-h-[92vh] overflow-y-auto overscroll-contain"
            role="dialog"
            aria-modal="true"
            aria-label={t.ariaLabel}
          >
            <button
              data-testid="simplify-close-button"
              onClick={close}
              aria-label="Close"
              className="absolute top-5 right-5 text-slate-400 hover:text-brand-navy transition-colors"
            >
              <X size={20} />
            </button>

            {done ? (
              <div data-testid="simplify-success" className="py-4">
                <span className="mx-auto w-14 h-14 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mb-6">
                  <CheckCircle2 size={26} />
                </span>
                <h3 data-testid="simplify-success-title" className="font-display font-semibold text-2xl text-brand-navy mb-3 text-center">
                  {form.followup === "phone" ? CALLBACK_COPY[locale].success : t.successTitle}
                </h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-sm mx-auto text-center mb-7">
                  {t.successBody}
                </p>
                {form.followup === "phone" && (
                  <div data-testid="callback-confirmation" className="mb-5 border-l-2 border-brand-teal pl-4 text-left">
                    <p className="text-xs font-semibold text-brand-teal">{CALLBACK_COPY[locale].requested}</p>
                    <p data-testid="callback-confirmation-window" className="mt-2 text-sm text-brand-navy break-words">{callbackSummary(form, locale)}</p>
                    <p data-testid="callback-confirmation-status" className="mt-2 text-xs text-slate-500">{CALLBACK_COPY[locale].note}</p>
                  </div>
                )}
                <div className="rounded-2xl bg-[#F8FAFC] border border-slate-100 p-5 space-y-3 text-left">
                  {(form.followup === "phone" ? [CALLBACK_COPY[locale].next, ...t.successSteps.slice(1)] : t.successSteps).map((s, i) => (
                    <p key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-600 leading-relaxed">
                      <span className="mt-0.5 w-4.5 w-5 h-5 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center font-mono text-[10px] font-bold shrink-0">
                        {i + 1}
                      </span>
                      {s}
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <span className="w-11 h-11 rounded-full bg-brand-navy text-brand-tealsoft flex items-center justify-center mb-5">
                  <Waves size={18} />
                </span>
                <h3 className="font-display font-semibold text-2xl text-brand-navy mb-2">
                  {t.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  {t.intro(step)}
                </p>

                <div className="flex gap-2 mb-7" aria-hidden="true">
                  {[1, 2].map((n) => (
                    <span
                      key={n}
                      className={`h-1 flex-1 rounded-full transition-colors duration-300 ${step >= n ? "bg-brand-teal" : "bg-slate-200"}`}
                    />
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  {step === 1 ? (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4"
                    >
                      <ul data-testid="simplify-expectations" className="mb-5 space-y-2 rounded-xl bg-[#F8FAFC] border border-slate-100 px-4 py-3.5">
                        {t.expectations.map((e) => (
                          <li key={e} className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
                            <span className="mt-1.5 h-1 w-1 rounded-full bg-brand-teal shrink-0" />
                            {e}
                          </li>
                        ))}
                      </ul>
                      <input
                        data-testid="simplify-input-name"
                        placeholder={t.namePh}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={inputCls}
                      />
                      <input
                        data-testid="simplify-input-email"
                        type="email"
                        placeholder={t.emailPh}
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={inputCls}
                      />
                      <button
                        data-testid="simplify-continue-button"
                        onClick={() => { if (canContinue) { trackFunnel({ event: "step1_completed", locale, source: pathname, step: 1 }); setStep(2); } }}
                        disabled={!canContinue}
                        className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-brand-navy text-white text-base font-semibold px-8 py-4 transition-all duration-300 hover:bg-brand-teal disabled:opacity-50"
                      >
                        {t.continue}
                        <ArrowRight size={17} />
                      </button>
                      <p className="text-xs text-slate-400 text-center">
                        {t.privacy}
                      </p>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.3 }}
                      onSubmit={submit}
                      className="space-y-4"
                    >
                      <input
                        data-testid="simplify-input-company"
                        placeholder={t.companyPh}
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        className={inputCls}
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <select
                          data-testid="simplify-input-industry"
                          value={form.industry}
                          onChange={(e) => setForm({ ...form, industry: e.target.value })}
                          className={inputCls}
                        >
                          {t.industries.map((i) => (
                            <option key={i}>{i}</option>
                          ))}
                        </select>
                        <select
                          data-testid="simplify-input-drain"
                          value={form.time_drain}
                          onChange={(e) => setForm({ ...form, time_drain: e.target.value })}
                          className={inputCls}
                        >
                          {t.drains.map((d) => (
                            <option key={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <select
                        data-testid="simplify-input-topic"
                        value={form.topic}
                        onChange={(e) => setForm({ ...form, topic: e.target.value })}
                        className={inputCls}
                        aria-label="Expert topic"
                      >
                        {t.topics.map((tp) => (
                          <option key={tp}>{tp}</option>
                        ))}
                      </select>
                      <div className="grid grid-cols-2 gap-3" role="group" aria-label={t.followupLabel}>
                        {["email", "phone"].map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`simplify-followup-${opt}`}
                            onClick={() => setForm({ ...form, followup: opt })}
                            aria-pressed={form.followup === opt}
                            className={`rounded-xl border-2 px-4 py-3 text-left text-xs font-medium leading-snug transition-all duration-300 ${
                              form.followup === opt
                                ? "border-brand-teal bg-brand-tealsoft text-brand-navy"
                                : "border-slate-200 text-slate-500 hover:border-brand-teal/60"
                            }`}
                          >
                            {opt === "email" ? t.followupEmail : t.followupPhone}
                          </button>
                        ))}
                      </div>
                      {form.followup === "phone" && (
                        <>
                        <input
                          data-testid="simplify-input-phone"
                          type="tel"
                          required
                          autoComplete="tel"
                          aria-label={t.phonePh}
                          placeholder={t.phonePh}
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className={inputCls}
                        />
                        <CallbackFields form={form} setForm={setForm} locale={locale} inputCls={inputCls} />
                        </>
                      )}
                      {submitError && <p data-testid="simplify-submit-error" role="alert" className="text-sm text-red-600">{submitError}</p>}
                      <button
                        data-testid="simplify-submit-button"
                        type="submit"
                        disabled={sending || (form.followup === "phone" && !form.phone.trim())}
                        className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-brand-navy text-white text-sm sm:text-base font-semibold px-5 py-4 transition-colors duration-300 hover:bg-brand-teal disabled:opacity-60"
                      >
                        {sending ? t.sending : t.submit}
                        {!sending && <ArrowRight size={17} />}
                      </button>
                      <button
                        type="button"
                        data-testid="simplify-back-button"
                        onClick={() => setStep(1)}
                        className="w-full inline-flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 hover:text-brand-navy transition-colors"
                      >
                        <ArrowLeft size={13} /> {t.back}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
